import { useCallback } from "react";
import useAudiencesReq from "../../../components/hook/use-req-aud";
import useRequest from "../../../components/hook/use-req";
import { Domain, Objective } from "./data";

// Audience Studio's AI layer — the "DALM Signal Hub" FastAPI service
// (server.py: POST /v1/usecases, /v1/planner, /v1/budget-plan), talking to
// Sarvam chat completions behind the scenes. `useAudiencesReq` already
// points at that service's base URL (REACT_APP_BACKEND_URL, defaulting to
// http://localhost:8000 for local dev). Every response carries a `source`
// of "sarvam" (live model) or "fallback" (the backend's own static content,
// returned whenever Sarvam is unreachable or misconfigured) — the UI badge
// in index.tsx reflects that. If the backend itself is down, `makeRequest`
// still resolves (see use-req-aud.tsx's catch), so callers here always fall
// back to data.ts's local content rather than throwing.

export type AiSource = "sarvam" | "fallback";

export interface UseCaseItem {
  name: string;
  description?: string;
}

export interface UseCasesResult {
  use_cases: UseCaseItem[];
  suggested_prompt?: string;
  source?: AiSource;
}

export interface PlannerSignal {
  signal_key: string;
  reason: string;
}

export interface PlannerResult {
  signals: PlannerSignal[];
  audience_name?: string;
  reason?: string;
  confidence?: string;
  source?: AiSource;
}

export interface BudgetAllocation {
  channel: string;
  percent: number;
}

export interface BudgetPlanResult {
  allocations: BudgetAllocation[];
  rationale?: string;
  primary_kpi?: string;
  source?: AiSource;
}

// Per (domain, objective) cache so switching back and forth in the same
// session doesn't re-burn a Sarvam call for content that hasn't changed
// (mirrors the reference demo_script.js's ucCache).
const useCaseCache = new Map<string, UseCasesResult>();

export function useUseCases() {
  const { loading, makeRequest } = useAudiencesReq("/usecases", "POST");

  const fetchUseCases = useCallback(
    async (domain: Domain, objective: Objective): Promise<UseCasesResult | null> => {
      const cacheKey = `${domain}|${objective}`;
      const cached = useCaseCache.get(cacheKey);
      if (cached) return cached;

      const [json, status] = await makeRequest({ domain, objective });
      if (status >= 200 && status < 300 && Array.isArray(json?.use_cases) && json.use_cases.length) {
        const result: UseCasesResult = {
          use_cases: json.use_cases,
          suggested_prompt: json.suggested_prompt,
          source: json.source,
        };
        useCaseCache.set(cacheKey, result);
        return result;
      }
      return null;
    },
    [makeRequest]
  );

  return { loading, fetchUseCases };
}

export function usePlanner() {
  const { loading, makeRequest } = useAudiencesReq("/planner", "POST");

  const requestPlan = useCallback(
    async (domain: Domain, objective: Objective, useCase: string | null, prompt: string): Promise<PlannerResult | null> => {
      const [json, status] = await makeRequest({ domain, objective, use_case: useCase, prompt });
      if (status >= 200 && status < 300 && Array.isArray(json?.signals) && json.signals.length) {
        return {
          signals: json.signals,
          audience_name: json.audience_name,
          reason: json.reason,
          confidence: json.confidence,
          source: json.source,
        };
      }
      return null;
    },
    [makeRequest]
  );

  return { loading, requestPlan };
}

export function useBudgetPlan() {
  const { loading, makeRequest } = useAudiencesReq("/budget-plan", "POST");

  const requestBudgetPlan = useCallback(
    async (params: {
      domain: Domain;
      objective: Objective;
      use_case: string | null;
      budget: number;
      currency: string;
      audience_size: number;
    }): Promise<BudgetPlanResult | null> => {
      const [json, status] = await makeRequest(params);
      if (status >= 200 && status < 300 && Array.isArray(json?.allocations) && json.allocations.length) {
        return {
          allocations: json.allocations,
          rationale: json.rationale,
          primary_kpi: json.primary_kpi,
          source: json.source,
        };
      }
      return null;
    },
    [makeRequest]
  );

  return { loading, requestBudgetPlan };
}

// ─── Domains / objectives catalog + AI-suggested objectives ────────────────
// Confirmed live against the same DALM Signal Hub service:
//   GET  /v1/domains                       → {domains:[{code,icon,description}]}
//     (only 4 domains today: Banking, Fintech, Ecommerce, Gaming — this is
//     now the sole source of truth for which domains exist; index.tsx no
//     longer keeps its own hardcoded domain list, which used to include 3
//     more domains the backend actually rejects with a 400 "unknown domain")
//   GET  /v1/domains/{domain}/signals       → {domain, signals:[{key,name,category,description,estimated_reach}]}
//   GET  /v1/objectives                     → {objectives:[{code,description}]}
//   POST /v1/objectives/suggest {domain}    → {objectives:[{code,description,rationale}], source}

export interface DomainItem {
  code: string;
  icon?: string;
  description?: string;
}

export function useDomainsCatalog() {
  const { loading, makeRequest } = useAudiencesReq("/domains", "GET");
  const fetchDomains = useCallback(async (): Promise<DomainItem[]> => {
    const [json, status] = await makeRequest();
    return status >= 200 && status < 300 && Array.isArray(json?.domains) ? json.domains : [];
  }, [makeRequest]);
  return { loading, fetchDomains };
}

export interface DomainSignal {
  key: string;
  name: string;
  category: string;
  description: string;
  estimated_reach: number;
}

export function useDomainSignals() {
  const { loading, makeRequest } = useAudiencesReq("/domains", "GET");
  const fetchSignals = useCallback(
    async (domain: Domain): Promise<DomainSignal[]> => {
      const [json, status] = await makeRequest(undefined, undefined, `${domain}/signals`);
      return status >= 200 && status < 300 && Array.isArray(json?.signals) ? json.signals : [];
    },
    [makeRequest]
  );
  return { loading, fetchSignals };
}

export interface ObjectiveItem {
  code: Objective;
  description: string;
}

export function useObjectivesCatalog() {
  const { loading, makeRequest } = useAudiencesReq("/objectives", "GET");
  const fetchObjectives = useCallback(async (): Promise<ObjectiveItem[]> => {
    const [json, status] = await makeRequest();
    return status >= 200 && status < 300 && Array.isArray(json?.objectives) ? json.objectives : [];
  }, [makeRequest]);
  return { loading, fetchObjectives };
}

export interface SuggestedObjective {
  code: Objective;
  description: string;
  rationale?: string;
}

export interface SuggestObjectivesResult {
  objectives: SuggestedObjective[];
  source?: AiSource;
}

export function useSuggestObjectives() {
  const { loading, makeRequest } = useAudiencesReq("/objectives/suggest", "POST");
  const suggestObjectives = useCallback(
    async (domain: Domain): Promise<SuggestObjectivesResult | null> => {
      const [json, status] = await makeRequest({ domain });
      if (status >= 200 && status < 300 && Array.isArray(json?.objectives)) {
        return { objectives: json.objectives, source: json.source };
      }
      return null;
    },
    [makeRequest]
  );
  return { loading, suggestObjectives };
}

// "Activate" on a recommendation — unlike the three hooks above, this hits
// the real core connect API (the same one Segmentation/Campaigns use), not
// the local AI-layer service, since it's turning the recommendation into an
// actual saved Segment record. There's no documented request contract for
// this endpoint yet (it's brand new), and the endpoint name/response shape
// ("planner/random", a server-picked name like "Planner - Kyc Tier
// (nested) 4791a0", a server-generated rule_tree) point to the backend
// synthesizing the segment itself for now — so this is a bodiless POST
// rather than us guessing at fields to send.
export interface PlannerSegment {
  id: string;
  name: string;
  source_scope: string;
  audience_id: string | null;
  generated_audience_id: string | null;
  created_at: string;
  contacts: number;
  valid_contacts: number;
  invalid_contacts: number;
  used_in: number;
  rule_tree: unknown;
}

export function useCreatePlannerSegment() {
  const token = localStorage.getItem("authToken");
  const { loading, makeRequest } = useRequest("/segments/planner/random", "POST", {
    Authorization: `Bearer ${token}`,
    "X-AUTH-TYPE": "jwt",
  });

  const createSegment = useCallback(async (): Promise<PlannerSegment | null> => {
    const [json, status] = await makeRequest({});
    if (status >= 200 && status < 300 && json?.status && json?.data?.id) {
      return json.data as PlannerSegment;
    }
    return null;
  }, [makeRequest]);

  return { loading, createSegment };
}
